// @ts-nocheck
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  FileText, Table2, Image, Archive, File, ChevronLeft,
  Loader2, AlertTriangle, CheckCircle2, XCircle, Clock,
  Upload, ArrowRight, RefreshCw,
} from 'lucide-react';
import { api } from '../utils/api.js';
import Logo from '../components/shared/Logo.jsx';

// ── Role/status → locked merchant copy (D2 table) ───────────────────────────
const FILE_STATUS_COPY: Record<string, Record<string, { label: string; color: string; bg: string }>> = {
  catalog: {
    parsed:                 { label: 'Catalog processed',                           color: 'var(--color-green)',  bg: 'var(--color-green-bg)' },
    queued:                 { label: 'Queued for extraction',                       color: 'var(--color-primary)', bg: 'var(--color-primary-light)' },
    parsing:                { label: 'Extracting products...',                      color: 'var(--color-primary)', bg: 'var(--color-primary-light)' },
    failed:                 { label: 'Extraction failed',                           color: 'var(--color-red)',    bg: 'var(--color-red-bg)' },
    pending:                { label: 'Waiting to process',                          color: 'var(--color-text-3)', bg: 'var(--color-surface-2)' },
    role_detected:          { label: 'Identified as catalog',                       color: 'var(--color-text-2)', bg: 'var(--color-surface-2)' },
  },
  price_sheet: {
    stored_pending_parser:  { label: 'Price sheet saved for reference',             color: 'var(--color-text-2)', bg: 'var(--color-surface-2)' },
    reconciled:             { label: 'Price sheet matched to products',             color: 'var(--color-green)',  bg: 'var(--color-green-bg)' },
  },
  order_form: {
    stored_pending_parser:  { label: 'Order form saved for reference',              color: 'var(--color-text-2)', bg: 'var(--color-surface-2)' },
    reconciled:             { label: 'Order form matched to products',              color: 'var(--color-green)',  bg: 'var(--color-green-bg)' },
  },
  inventory_sheet: {
    stored_pending_parser:  { label: 'Inventory sheet saved for reference',          color: 'var(--color-text-2)', bg: 'var(--color-surface-2)' },
    reconciled:             { label: 'Inventory matched to products',               color: 'var(--color-green)',  bg: 'var(--color-green-bg)' },
  },
  spec_sheet: {
    stored_pending_parser:  { label: 'Spec sheet saved for reference',              color: 'var(--color-text-2)', bg: 'var(--color-surface-2)' },
    reconciled:             { label: 'Spec sheet matched to products',              color: 'var(--color-green)',  bg: 'var(--color-green-bg)' },
  },
  lookbook: {
    stored_pending_parser:  { label: 'Lookbook saved for reference',                color: 'var(--color-text-2)', bg: 'var(--color-surface-2)' },
    reconciled:             { label: 'Lookbook matched to products',                color: 'var(--color-green)',  bg: 'var(--color-green-bg)' },
  },
  edi: {
    stored_pending_parser:  { label: 'EDI file saved for reference',                color: 'var(--color-text-2)', bg: 'var(--color-surface-2)' },
    reconciled:             { label: 'EDI data matched to products',                color: 'var(--color-green)',  bg: 'var(--color-green-bg)' },
  },
  image_archive: {
    stored_pending_parser:  { label: 'Image archive saved — will be linked to products after extraction', color: 'var(--color-text-2)', bg: 'var(--color-surface-2)' },
  },
  product_images: {
    stored_pending_parser:  { label: 'Product images saved — will be linked after extraction', color: 'var(--color-text-2)', bg: 'var(--color-surface-2)' },
  },
  terms_reference: {
    ignored_reference:      { label: 'Terms & conditions — skipped (not product data)', color: 'var(--color-text-3)', bg: 'var(--color-surface-2)' },
  },
  unknown: {
    unsupported_file_type:  { label: 'File type not supported',                     color: 'var(--color-yellow)', bg: 'var(--color-yellow-bg)' },
    rejected_unsafe:        { label: 'File rejected — security check failed',       color: 'var(--color-red)',    bg: 'var(--color-red-bg)' },
  },
};

function getFileCopy(role: string, parseStatus: string) {
  const roleMap = FILE_STATUS_COPY[role] || FILE_STATUS_COPY.unknown;
  return roleMap?.[parseStatus] || { label: parseStatus, color: 'var(--color-text-3)', bg: 'var(--color-surface-2)' };
}

function getFileIcon(name: string) {
  const ext = name.split('.').pop()?.toLowerCase() || '';
  if (ext === 'pdf') return FileText;
  if (['xlsx', 'xls', 'csv'].includes(ext)) return Table2;
  if (['jpg', 'jpeg', 'png', 'webp', 'gif', 'heic', 'heif'].includes(ext)) return Image;
  if (ext === 'zip') return Archive;
  return File;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

const DROP_STATUS_LABELS: Record<string, { label: string; Icon: any; color: string }> = {
  pending:               { label: 'Processing',           Icon: Loader2,       color: 'var(--color-primary)' },
  role_detection:        { label: 'Analyzing files',      Icon: Loader2,       color: 'var(--color-primary)' },
  starting_extraction:   { label: 'Starting extraction',  Icon: Loader2,       color: 'var(--color-primary)' },
  extracting:            { label: 'Extracting products',  Icon: Loader2,       color: 'var(--color-primary)' },
  reconciling:           { label: 'Matching companion files', Icon: Loader2,  color: 'var(--color-primary)' },
  review_ready:          { label: 'Ready for review',     Icon: CheckCircle2,  color: 'var(--color-green)' },
  needs_user_decision:   { label: 'Action needed',        Icon: AlertTriangle, color: 'var(--color-yellow)' },
  failed:                { label: 'Failed',               Icon: XCircle,       color: 'var(--color-red)' },
};

// ── Drop Detail ──────────────────────────────────────────────────────────────

function DropDetail() {
  const { catalog_drop_id } = useParams<{ catalog_drop_id: string }>();
  const navigate = useNavigate();
  const [drop, setDrop] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selecting, setSelecting] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchDrop = useCallback(async () => {
    try {
      const data = await api.getCatalogDrop(catalog_drop_id);
      setDrop(data);
      setError('');

      // Stop polling once in a terminal state
      if (['review_ready', 'failed', 'needs_user_decision'].includes(data.status)) {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load drop');
    } finally {
      setLoading(false);
    }
  }, [catalog_drop_id]);

  useEffect(() => {
    fetchDrop();
    // Poll while in progress
    pollRef.current = setInterval(fetchDrop, 4000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchDrop]);

  // Auto-navigate to review when ready
  useEffect(() => {
    if (drop?.status === 'review_ready' && drop?.primary_catalog) {
      navigate(drop.primary_catalog.review_url, { replace: true });
    }
  }, [drop, navigate]);

  async function handleSelectPrimary(catalogFileId: string) {
    setSelecting(catalogFileId);
    try {
      const result = await api.selectPrimary(catalog_drop_id!, catalogFileId);
      if (result.catalog_id && result.job_id) {
        navigate(`/review/${result.catalog_id}?job_id=${result.job_id}`);
      } else {
        await fetchDrop();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to select primary file');
    } finally {
      setSelecting(null);
    }
  }

  if (loading) {
    return (
      <PageShell navigate={navigate}>
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <Loader2 size={32} style={{ color: 'var(--color-primary)', animation: 'spin 0.75s linear infinite' }} />
        </div>
      </PageShell>
    );
  }

  if (error && !drop) {
    return (
      <PageShell navigate={navigate}>
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <XCircle size={32} style={{ color: 'var(--color-red)', marginBottom: 12 }} />
          <div style={{ fontSize: 16, color: 'var(--color-text-2)' }}>{error}</div>
        </div>
      </PageShell>
    );
  }

  if (!drop) return null;

  const statusInfo = DROP_STATUS_LABELS[drop.status] || DROP_STATUS_LABELS.pending;
  const isInProgress = ['pending', 'role_detection', 'starting_extraction', 'extracting'].includes(drop.status);

  // Brief V1-Finishing v2 D.1 / DV1.17 — picker reads from drop.decision_required
  // (provided by D.0.1 backend extension). Handles both multi-PDF and
  // all-spreadsheet ambiguity per spec §3.10.3 rules 4 + 6.
  const decisionRequired = drop.decision_required;
  const pickerOptions = decisionRequired?.options || [];
  const showPicker = drop.status === 'needs_user_decision' && pickerOptions.length > 1;
  const pickerFileIds = new Set(pickerOptions.map((o: any) => o.catalog_file_id));
  const recommendedScore = pickerOptions.reduce((m: number, o: any) => Math.max(m, o.raw_score ?? 0), 0);

  return (
    <PageShell navigate={navigate}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Partial-success warning header (v4.6-8) */}
      {drop.status === 'failed' && drop.failure_stage && (
        <div style={{
          background: 'var(--color-red-bg)', border: '1px solid var(--color-red-border)',
          color: 'var(--color-red-text)', borderRadius: 10, padding: '12px 16px',
          marginBottom: 20, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <AlertTriangle size={16} />
          {drop.failure_stage === 'extraction_failed'
            ? 'Extraction failed. Please try uploading again or contact support if the issue persists.'
            : drop.failure_stage === 'stuck_starting_extraction'
            ? 'Processing timed out. Please try uploading again.'
            : 'Something went wrong. Please try uploading again.'}
        </div>
      )}

      {error && (
        <div style={{
          background: 'var(--color-red-bg)', border: '1px solid var(--color-red-border)',
          color: 'var(--color-red-text)', borderRadius: 10, padding: '12px 16px',
          marginBottom: 20, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <AlertTriangle size={16} />
          {error}
        </div>
      )}

      {/* Drop header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
        <statusInfo.Icon
          size={20}
          style={{
            color: statusInfo.color, flexShrink: 0,
            ...(isInProgress ? { animation: 'spin 0.75s linear infinite' } : {}),
          }}
        />
        <div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{statusInfo.label}</div>
          <div style={{ fontSize: 13, color: 'var(--color-text-3)' }}>
            {drop.files?.length || 0} file{(drop.files?.length || 0) !== 1 ? 's' : ''} uploaded
            {' '}&middot;{' '}
            {new Date(drop.created_at).toLocaleDateString()}
          </div>
        </div>
      </div>

      {/* Needs user decision: evidence-rich primary picker */}
      {showPicker && (
        <PickerCard
          reason={decisionRequired.reason}
          message={decisionRequired.message}
          options={pickerOptions}
          recommendedScore={recommendedScore}
          selecting={selecting}
          onSelect={handleSelectPrimary}
        />
      )}

      {/* no_catalog_detected blocking banner (Brief V1-Finishing v2 D.3) */}
      {drop.status === 'no_catalog_detected' && (
        <NoCatalogDetectedBanner />
      )}

      {/* Per-file section — hide picker candidates to avoid duplicate cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {(drop.files || []).filter((f: any) => {
          if (showPicker) return !pickerFileIds.has(f.id);
          return true;
        }).map((f: any) => {
          const Icon = getFileIcon(f.original_filename);
          const copy = getFileCopy(f.role_guess, f.parse_status);
          return (
            <div key={f.id} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 14px', borderRadius: 8,
              background: 'var(--color-surface)', border: '1px solid var(--color-border)',
            }}>
              <Icon size={18} style={{ color: 'var(--color-primary)', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {f.original_filename}
                </div>
                <div style={{ fontSize: 12, color: copy.color, marginTop: 2 }}>
                  {copy.label}
                </div>
              </div>
              <span style={{ fontSize: 12, color: 'var(--color-text-3)', flexShrink: 0 }}>
                {formatSize(f.size_bytes)}
              </span>
            </div>
          );
        })}
      </div>

      {/* Review ready with catalog link */}
      {drop.status === 'review_ready' && drop.primary_catalog && (
        <button
          onClick={() => navigate(drop.primary_catalog.review_url)}
          style={{
            ...primaryBtn, marginTop: 24,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}
        >
          Review Products
          <ArrowRight size={16} />
        </button>
      )}

      {/* Failed: retry */}
      {drop.status === 'failed' && (
        <button
          onClick={() => navigate('/upload')}
          style={{
            ...primaryBtn, marginTop: 24,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}
        >
          <RefreshCw size={16} />
          Try Again
        </button>
      )}

      {/* Extracting: processing indicator */}
      {isInProgress && (
        <div style={{
          textAlign: 'center', padding: '24px 0', color: 'var(--color-text-3)', fontSize: 13,
        }}>
          Processing your files... This may take a few minutes.
        </div>
      )}
    </PageShell>
  );
}

// ── Picker / banner subcomponents (Brief V1-Finishing v2 D.1, D.3) ──────────

function pickerTitleCopy(reason: string): string {
  // Mirrors COPY.md §10.X picker UI rows.
  if (reason === 'multiple_catalog_pdfs') return 'Multiple catalogs detected';
  if (reason === 'multiple_catalog_spreadsheets') return 'Multiple catalog spreadsheets';
  return 'Pick a primary catalog';
}

function pickerBodyCopy(reason: string): string {
  if (reason === 'multiple_catalog_pdfs') {
    return 'We found multiple PDFs that look like catalogs. Choose the primary catalog source.';
  }
  if (reason === 'multiple_catalog_spreadsheets') {
    return 'We found multiple spreadsheets that look like catalogs. Choose the primary.';
  }
  return 'Select the primary catalog to extract products from.';
}

function PickerCard({ reason, message, options, recommendedScore, selecting, onSelect }: any) {
  return (
    <div style={{
      background: 'var(--color-yellow-bg)', border: '1px solid var(--color-yellow-border)',
      borderRadius: 10, padding: '16px', marginBottom: 20,
    }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-yellow-text)', marginBottom: 8 }}>
        {pickerTitleCopy(reason)}
      </div>
      <div style={{ fontSize: 13, color: 'var(--color-yellow-text)', marginBottom: 12, lineHeight: 1.5 }}>
        {message || pickerBodyCopy(reason)}
      </div>
      {/* Tie-badge fix (DPP.1, 2026-05-11) — when N candidates share the max
          score, "Recommended" is meaningless ("this one, not the other" doesn't
          apply when both equal). Suppress badge on all tied candidates. */}
      {(() => {
        const tiedAtMax = recommendedScore > 0
          && options.filter((o: any) => (o.raw_score ?? 0) === recommendedScore).length > 1;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {options.map((opt: any) => (
              <PickerCandidate
                key={opt.catalog_file_id}
                option={opt}
                isRecommended={!tiedAtMax && (opt.raw_score ?? 0) > 0 && opt.raw_score === recommendedScore}
                selecting={selecting}
                onSelect={onSelect}
              />
            ))}
          </div>
        );
      })()}
    </div>
  );
}

function PickerCandidate({ option, isRecommended, selecting, onSelect }: any) {
  const fileId = option.catalog_file_id;
  const filename = option.original_filename || 'Unnamed file';
  const sizeBytes = option.size_bytes ?? 0;
  const confidencePct = option.role_confidence != null ? Math.round(option.role_confidence * 100) : null;
  const sampledCount = option.sampled_product_count ?? null;
  const posEvidence = option.positive_evidence_summary || '';
  const negEvidence = option.negative_evidence_summary || '';
  const isBusy = selecting && selecting === fileId;
  const isDisabled = !!selecting && selecting !== fileId;

  return (
    <button
      onClick={() => onSelect(fileId)}
      disabled={!!selecting}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 8,
        padding: '12px 14px', borderRadius: 8,
        background: 'var(--color-surface)',
        border: isRecommended
          ? '1.5px solid var(--color-primary)'
          : '1px solid var(--color-border)',
        cursor: selecting ? 'not-allowed' : 'pointer',
        opacity: isDisabled ? 0.5 : 1,
        textAlign: 'left', width: '100%',
      }}
    >
      {/* Top row: icon, filename, size, recommended badge, chevron */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {isBusy ? (
          <Loader2 size={16} style={{ animation: 'spin 0.75s linear infinite', color: 'var(--color-primary)', flexShrink: 0 }} />
        ) : (
          <FileText size={16} style={{ color: 'var(--color-primary)', flexShrink: 0 }} />
        )}
        <span style={{ flex: 1, fontSize: 14, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {filename}
        </span>
        {isRecommended && (
          <span style={{
            fontSize: 11, fontWeight: 600, color: 'var(--color-primary)',
            background: 'var(--color-primary-light)', padding: '2px 8px', borderRadius: 4,
            flexShrink: 0,
          }}>
            Recommended
          </span>
        )}
        <span style={{ fontSize: 12, color: 'var(--color-text-3)', flexShrink: 0 }}>
          {formatSize(sizeBytes)}
        </span>
        <ArrowRight size={14} style={{ color: 'var(--color-text-3)', flexShrink: 0 }} />
      </div>

      {/* Evidence row: confidence + sampled + tokens — gracefully degrades when nulls */}
      {(confidencePct != null || sampledCount != null) && (
        <div style={{ fontSize: 12, color: 'var(--color-text-2)', paddingLeft: 26, display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          {confidencePct != null && (
            <span>Catalog &middot; {confidencePct}% confidence</span>
          )}
          {sampledCount != null && sampledCount > 0 && (
            <span>Sampled {sampledCount} product{sampledCount === 1 ? '' : 's'}</span>
          )}
        </div>
      )}

      {posEvidence && (
        <div style={{ fontSize: 12, color: 'var(--color-green)', paddingLeft: 26, display: 'flex', alignItems: 'flex-start', gap: 6 }}>
          <CheckCircle2 size={12} style={{ marginTop: 2, flexShrink: 0 }} />
          <span>{posEvidence}</span>
        </div>
      )}
      {negEvidence && (
        <div style={{ fontSize: 12, color: 'var(--color-yellow-text)', paddingLeft: 26, display: 'flex', alignItems: 'flex-start', gap: 6 }}>
          <AlertTriangle size={12} style={{ marginTop: 2, flexShrink: 0 }} />
          <span>{negEvidence}</span>
        </div>
      )}
    </button>
  );
}

function NoCatalogDetectedBanner() {
  return (
    <div style={{
      background: 'var(--color-red-bg)', border: '1px solid var(--color-red-border)',
      borderRadius: 10, padding: '16px', marginBottom: 20,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 12 }}>
        <AlertTriangle size={18} style={{ color: 'var(--color-red)', flexShrink: 0, marginTop: 2 }} />
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-red-text)', marginBottom: 4 }}>
            No catalog content detected
          </div>
          <div style={{ fontSize: 13, color: 'var(--color-red-text)', lineHeight: 1.5 }}>
            We couldn't find a product catalog in this upload. Suggestions:
          </div>
        </div>
      </div>
      <ul style={{ margin: '0 0 12px 36px', padding: 0, fontSize: 13, color: 'var(--color-red-text)', lineHeight: 1.7 }}>
        <li>Rename files with role keywords (e.g. "catalog")</li>
        <li>Upload a PDF/XLSX/CSV with product data</li>
        <li>Contact support if you believe this is an error</li>
      </ul>
      <div style={{ paddingLeft: 36 }}>
        <a href="mailto:support@catalogconvert.com" style={{
          fontSize: 13, fontWeight: 600, color: 'var(--color-red)',
          textDecoration: 'underline',
        }}>
          Contact support
        </a>
      </div>
    </div>
  );
}

// ── Drop List ────────────────────────────────────────────────────────────────

function DropList() {
  const navigate = useNavigate();
  const [drops, setDrops] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api.getCatalogDrops({ limit: 50 })
      .then((data: any) => setDrops(data.drops || []))
      .catch((err: any) => setError(err.message || 'Failed to load drops'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <PageShell navigate={navigate}>
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <Loader2 size={32} style={{ color: 'var(--color-primary)', animation: 'spin 0.75s linear infinite' }} />
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell navigate={navigate}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 4 }}>Uploads</h1>
      <p style={{ color: 'var(--color-text-2)', marginBottom: 24, fontSize: 14 }}>
        Your uploaded supplier drops and their processing status.
      </p>

      {error && (
        <div style={{
          background: 'var(--color-red-bg)', border: '1px solid var(--color-red-border)',
          color: 'var(--color-red-text)', borderRadius: 10, padding: '12px 16px',
          marginBottom: 20, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <AlertTriangle size={16} />
          {error}
        </div>
      )}

      {drops.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '64px 24px',
          border: '2px dashed var(--color-border)', borderRadius: 14,
          background: 'var(--color-surface)',
        }}>
          <Upload size={36} style={{ color: 'var(--color-primary)', marginBottom: 12 }} />
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>
            No drops yet
          </div>
          <div style={{ color: 'var(--color-text-3)', fontSize: 14, marginBottom: 20, lineHeight: 1.5 }}>
            Upload your first supplier drop to get started.
          </div>
          <button onClick={() => navigate('/upload')} style={primaryBtn}>
            Upload Catalog
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {drops.map((d: any) => {
            const statusInfo = DROP_STATUS_LABELS[d.status] || DROP_STATUS_LABELS.pending;
            const isActive = ['pending', 'role_detection', 'starting_extraction', 'extracting'].includes(d.status);
            return (
              <Link
                key={d.catalog_drop_id}
                to={`/drops/${d.catalog_drop_id}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '14px 16px', borderRadius: 10,
                  background: 'var(--color-surface)', border: '1px solid var(--color-border)',
                  textDecoration: 'none', color: 'inherit',
                  transition: 'border-color 0.15s',
                }}
              >
                <statusInfo.Icon
                  size={18}
                  style={{
                    color: statusInfo.color, flexShrink: 0,
                    ...(isActive ? { animation: 'spin 0.75s linear infinite' } : {}),
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {d.primary_filename || `Upload — ${d.file_count} file${d.file_count !== 1 ? 's' : ''}`}
                  </div>
                  <div style={{ fontSize: 12, color: statusInfo.color, marginTop: 2 }}>
                    {statusInfo.label}
                  </div>
                </div>
                <div style={{ fontSize: 12, color: 'var(--color-text-3)', flexShrink: 0 }}>
                  {new Date(d.created_at).toLocaleDateString()}
                </div>
                <ArrowRight size={14} style={{ color: 'var(--color-text-3)', flexShrink: 0 }} />
              </Link>
            );
          })}
        </div>
      )}
    </PageShell>
  );
}

// ── Page shell ───────────────────────────────────────────────────────────────

function PageShell({ navigate, children }: { navigate: any; children: React.ReactNode }) {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-bg)' }}>
      <header style={{ background: 'var(--color-surface)', borderBottom: '1px solid var(--color-border)' }}>
        <div style={{ maxWidth: 800, margin: '0 auto', padding: '0 24px', height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Logo iconSize={24} onClick={() => navigate('/dashboard')} />
          <button onClick={() => navigate('/dashboard')} style={ghostBtn}>
            <ChevronLeft size={14} style={{ marginRight: 4 }} />
            Dashboard
          </button>
        </div>
      </header>
      <main style={{ maxWidth: 560, margin: '0 auto', padding: '48px 24px' }}>
        {children}
      </main>
    </div>
  );
}

// ── Styles ───────────────────────────────────────────────────────────────────

const primaryBtn: React.CSSProperties = {
  width: '100%', background: 'var(--color-primary)', color: '#fff',
  border: 'none', borderRadius: 10, padding: '14px',
  fontSize: 16, fontWeight: 700, cursor: 'pointer',
};

const ghostBtn: React.CSSProperties = {
  background: 'none', border: '1px solid var(--color-border)',
  borderRadius: 8, padding: '6px 14px', fontSize: 13,
  color: 'var(--color-text-2)', cursor: 'pointer',
  display: 'flex', alignItems: 'center',
};

// ── Exports ──────────────────────────────────────────────────────────────────

export { DropDetail, DropList };
export default DropDetail;
